import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ArrowLeft, Palette, Box, FileText, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const fetchAPI = async (endpoint, options = {}) => {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Erro na requisição');
  }
  return response.json();
};

const Form3D = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const surveyId = searchParams.get('survey_id');

  const [formData, setFormData] = useState({
    internal_color_id: '',
    external_color_id: '',
    handle_model_id: '',
    handle_reference: '',
    general_observations: '',
    form_3d_completed: false
  });

  // Buscar dados do levantamento
  const { data: surveyData, isLoading: surveyLoading } = useQuery({
    queryKey: ['survey', surveyId],
    queryFn: () => fetchAPI(`/surveys/${surveyId}`),
    enabled: !!surveyId,
  });

  // Buscar opções de acabamento
  const { data: optionsData, isLoading: optionsLoading } = useQuery({
    queryKey: ['form-3d-options'],
    queryFn: () => fetchAPI('/form-3d/options'),
  });

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data) => fetchAPI(`/surveys/${surveyId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['survey', surveyId]);
      toast.success('Formulário salvo com sucesso!');
      navigate('/levantamentos');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  // Preencher dados do levantamento quando carregarem
  useEffect(() => {
    if (surveyData?.data?.survey) {
      const survey = surveyData.data.survey;
      setFormData({
        internal_color_id: survey.internal_color_id || '',
        external_color_id: survey.external_color_id || '',
        handle_model_id: survey.handle_model_id || '',
        handle_reference: survey.handle_reference || '',
        general_observations: survey.general_observations || '',
        form_3d_completed: survey.form_3d_completed || false
      });
    }
  }, [surveyData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  const handleSaveAndComplete = (e) => {
    e.preventDefault();
    saveMutation.mutate({
      ...formData,
      form_3d_completed: true
    });
  };

  const options = optionsData?.data || {};
  const internalColors = options.internalColors || [];
  const externalColors = options.externalColors || [];
  const handleModels = options.handleModels || [];

  if (surveyLoading || optionsLoading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-center text-gray-600">Carregando...</p>
      </div>
    );
  }

  const survey = surveyData?.data?.survey || {};

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/levantamentos')}
          className="text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Formulário de Acabamento 3D</h1>
          <p className="text-gray-600">
            Levantamento: {survey.survey_code} - {survey.client_name}
          </p>
        </div>
      </div>

      {/* Info do Levantamento */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <p className="text-blue-800">
          <strong>Status atual:</strong> {survey.status}
        </p>
      </div>

      <form className="space-y-6">
        {/* Cores Internas */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Palette className="w-5 h-5 text-purple-600" />
            Cor da Parte Interna
          </h2>
          <select
            name="internal_color_id"
            value={formData.internal_color_id}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Selecione uma cor</option>
            {internalColors.map(color => (
              <option key={color.id} value={color.id}>
                {color.name}
              </option>
            ))}
          </select>
        </div>

        {/* Cores Externas */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Palette className="w-5 h-5 text-blue-600" />
            Cor da Parte Externa
          </h2>
          <select
            name="external_color_id"
            value={formData.external_color_id}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Selecione uma cor</option>
            {externalColors.map(color => (
              <option key={color.id} value={color.id}>
                {color.name}
              </option>
            ))}
          </select>
        </div>

        {/* Puxador - Do sistema de estoque */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <Box className="w-5 h-5 text-green-600" />
            Puxador (do Estoque)
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Selecione um puxador cadastrado no sistema de Estoque → Produtos (categoria Puxadores)
          </p>
          <select
            name="handle_model_id"
            value={formData.handle_model_id}
            onChange={handleChange}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 mb-4"
          >
            <option value="">Selecione um modelo</option>
            {handleModels.map(model => (
              <option key={model.id} value={model.id}>
                {model.name} {model.reference_code ? `(${model.reference_code})` : ''}
              </option>
            ))}
          </select>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Referência do Puxador
            </label>
            <input
              type="text"
              name="handle_reference"
              value={formData.handle_reference}
              onChange={handleChange}
              placeholder="Ex: Referência do catálogo ou loja"
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Observações Gerais */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-gray-600" />
            Observações Gerais
          </h2>
          <textarea
            name="general_observations"
            value={formData.general_observations}
            onChange={handleChange}
            rows={4}
            placeholder="Observações adicionais sobre o acabamento..."
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gray-500"
          />
        </div>

        {/* Botões */}
        <div className="flex gap-4">
          <button
            type="button"
            onClick={handleSave}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <Save className="w-5 h-5" />
            Salvar Rascunho
          </button>

          <button
            type="button"
            onClick={handleSaveAndComplete}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <CheckCircle className="w-5 h-5" />
            Salvar e Finalizar (Apresentar ao Cliente)
          </button>
        </div>

        <p className="text-sm text-gray-500 text-center">
          Ao clicar em "Salvar e Finalizar", o levantamento será marcado como apresentado e retornará para o gerente.
        </p>
      </form>
    </div>
  );
};

export default Form3D;
